import { ParsedComment } from "@/jenesys-types/data/communication";
import { AppError } from "@/jenesys-types/errors";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface CommentParsingState {
  parsedComments: Record<string, ParsedComment>; // key: commentId
  isLoading: boolean;
  error: AppError | null;
}

const initialState: CommentParsingState = {
  parsedComments: {},
  isLoading: false,
  error: null
};

export const commentParsingSlice = createSlice({
  name: "state/features/CommentParsing",
  initialState,
  reducers: {
    parseComment: {
      reducer: (state: CommentParsingState) => {
        state.isLoading = true;
        state.error = null;
      },
      prepare: (commentId: string, rawText: string, companyId: string) => ({
        payload: { commentId, rawText, companyId }
      })
    },
    parseCommentSuccess: {
      reducer: (
        state: CommentParsingState,
        action: PayloadAction<{
          commentId: string;
          parsedComment: ParsedComment;
        }>
      ) => {
        state.isLoading = false;
        state.parsedComments[action.payload.commentId] =
          action.payload.parsedComment;
      },
      prepare: (commentId: string, parsedComment: ParsedComment) => ({
        payload: { commentId, parsedComment }
      })
    },
    parseCommentFailure: {
      reducer: (
        state: CommentParsingState,
        action: PayloadAction<AppError>
      ) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      prepare: (error: AppError) => ({ payload: error })
    }
  }
});

export const { parseComment, parseCommentSuccess, parseCommentFailure } =
  commentParsingSlice.actions;

// Selectors
export const selectCommentParsingState = (state: {
  features: { CommentParsing: CommentParsingState };
}): CommentParsingState => state.features.CommentParsing;

export const selectParsedComments = createSelector(
  selectCommentParsingState,
  (state) => state.parsedComments
);

export const selectParsedComment = (commentId: string) =>
  createSelector(
    selectCommentParsingState,
    (state) => state.parsedComments[commentId]
  );

export const selectIsCommentParsing = createSelector(
  selectCommentParsingState,
  (state) => state.isLoading
);

export const selectCommentParsingError = createSelector(
  selectCommentParsingState,
  (state) => state.error
);

export default commentParsingSlice.reducer;
